import React from 'react';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';

import {ConFormula, PtrnCampoEditableFormula} from '../../../patronTypes';
import {EditableProps} from '../campoEditableTypes';
import {getNoBorderSx, useInputStyles} from '../campoEditableUtils';
import {PtrnPropsUtil} from '../../../patronUtils';
import {useBoolean} from '../../../../../hooks';
import FormulaIcon from '../../PtrnIcon/FormulaIcon';

export type CampoEditableFormulaProps<T> = EditableProps<ConFormula<T>> & {
   campoConfig: PtrnCampoEditableFormula;
   ErrorComponent?: React.ReactNode;
   onCopyFormulaRef?: (formulaRef) => void;
};

const CampoEditableFormula = <T,>(props: CampoEditableFormulaProps<T>) => {
   const {campoConfig, ...editableProps} = props;
   const auxParam = {
      camposData: editableProps.camposData,
      campoConfig: campoConfig,
      ptrnRef: editableProps.ptrnRef,
      ruta: editableProps.ruta,
      index: editableProps.index
   };
   const classes = useInputStyles();

   const [isEditing, {setFalse: setIsNotEditing, setTrue: setIsEditing}] = useBoolean(false);

   const getNewValueFromEvent = (
      e: React.ChangeEvent<HTMLInputElement> | React.FocusEvent<HTMLInputElement, Element>
   ) => {
      const newEventValue = e.target.value;
      let newValue: ConFormula<T> = editableProps.value
         ? {...editableProps.value}
         : {formula: '', valorVisible: '', valor: null};
      if (newEventValue.startsWith('=')) {
         newValue.formula = newEventValue;
         newValue.valorVisible = campoConfig.mostrarFormulaOnBlur
            ? newValue.formula
            : newEventValue;
      } else {
         newValue.formula = '';
         newValue.valorVisible = newEventValue;
      }
      return newValue;
   };

   const handleOnChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
      const name = e.target.name;
      const newValue = getNewValueFromEvent(e);

      editableProps.onChange(name, newValue, auxParam);
   };

   const handleOnBlur: React.FocusEventHandler<HTMLInputElement> = (e) => {
      if (editableProps.onBlur) {
         const name = e.target.name;
         const newValue = getNewValueFromEvent(e);
         editableProps.onBlur(name, newValue, auxParam);
      }
      setIsNotEditing();
   };
   const handleOnFocus: React.FocusEventHandler<HTMLInputElement> = (e) => {
      setIsEditing();
      setTimeout(() => e.target.select(), 0); // Asegura la selección completa del texto
   };

   //#region GEN PROPS
   const id = PtrnPropsUtil.genIdCampoEditable(campoConfig);
   //boton referencia
   const formulaRefButton =
      campoConfig.mostrarCopiarReferencia &&
      campoConfig.formulaRef &&
      campoConfig.onCopyFormulaRef ? (
         <IconButton
            title="Copiar formula"
            size="small"
            onClick={() => campoConfig.onCopyFormulaRef(campoConfig.formulaRef)}
         >
            <FormulaIcon />
         </IconButton>
      ) : undefined;
   //adornments
   const endAdornments =
      editableProps.ErrorComponent || formulaRefButton ? (
         <InputAdornment position="end">
            {formulaRefButton}
            {editableProps.ErrorComponent}
         </InputAdornment>
      ) : undefined;
   //#endregion

   let visible = '';
   if (editableProps.value) {
      if (isEditing) {
         visible = editableProps.value.formula.startsWith('=')
            ? editableProps.value.formula
            : editableProps.value.valorVisible;
      } else {
         visible = campoConfig.generarValorMostrar
            ? campoConfig.generarValorMostrar(editableProps.value.valorVisible)
            : editableProps.value.valorVisible;
      }
   }
   //#region RETORNOS
   return (
      <FormControl fullWidth error={Boolean(editableProps.ErrorComponent)}>
         <InputLabel htmlFor={id}>{campoConfig.label}</InputLabel>
         <OutlinedInput
            sx={getNoBorderSx(campoConfig)}
            className={classes.classes.ceFocused}
            id={id}
            name={campoConfig.name}
            label={campoConfig.label}
            value={visible}
            onChange={handleOnChange}
            onBlur={handleOnBlur}
            onFocus={handleOnFocus}
            fullWidth
            endAdornment={endAdornments}
            inputProps={{
               style: {textAlign: campoConfig.alinearTexto || 'left'}
            }}
            size="small"
         />
      </FormControl>
   );
   //#endregion
};

export default CampoEditableFormula;
